import React, { useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setStrategy } from '../../../redux/actions'
import { useAuth0 } from '../../../contexts/auth0-context'
import callAPI from '../../../API/callSecureAPI'
import {
    Box,
    Typography,
    Paper,
    Chip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    FormControlLabel,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    Tooltip,
    CircularProgress,
    LinearProgress,
    Alert,
    Collapse,
} from '@mui/material'
import SvgIcon from '@mui/material/SvgIcon'
import { ReactComponent as PushPinSvg } from '../../../assets/images/PushPin.svg'
import {
    useAdminAnalyticsQuery,
    AnalyticsRoadmap,
    AnalyticsOrganization,
    useAdminRoadmapChangesQuery,
    RoadmapChange,
} from '../../../shared/queries/admin'

const RoadmapAnalyticsTab: React.FC = () => {
    const history = useHistory()
    const location = useLocation()
    const dispatch = useDispatch()
    const { getTokenSilently } = useAuth0()
    const [excludeInternal, setExcludeInternal] = useState(false)
    const ALL_ACTION_TYPES = [
        'CREATE',
        'UPDATE',
        'DELETE',
        'AI_UPLOAD',
        'AI_SUMMARY',
        'PPT_EXPORT',
        'SNAPSHOT',
        'MAIA_CHAT',
        'CHANGE_ROADMAP',
    ]
    const ACTION_TYPE_LABELS: Record<string, string> = {
        CREATE: 'Create',
        UPDATE: 'Update',
        DELETE: 'Delete',
        AI_UPLOAD: 'AI Upload',
        AI_SUMMARY: 'AI Summary',
        PPT_EXPORT: 'PPT Export',
        SNAPSHOT: 'Snapshot',
        MAIA_CHAT: 'MAIA Chat',
        CHANGE_ROADMAP: 'Change Roadmap',
    }
    const [selectedActionTypes, setSelectedActionTypes] = useState<string[]>(ALL_ACTION_TYPES)
    const { data, isLoading, error } = useAdminAnalyticsQuery({
        includeInternal: !excludeInternal,
        actionTypes: selectedActionTypes.length < ALL_ACTION_TYPES.length ? selectedActionTypes : undefined,
    })
    const [selectedOrg, setSelectedOrg] = useState<string | null>(null)
    const [sortBy, setSortBy] = useState<'size' | 'activity' | 'name'>('size')
    const [sortOrgsBy, setSortOrgsBy] = useState<'activity' | 'name' | 'favorites'>('activity')
    const [changesDialog, setChangesDialog] = useState<{ open: boolean; roadmap: AnalyticsRoadmap | null }>({
        open: false,
        roadmap: null,
    })
    const [showAllChanges, setShowAllChanges] = useState(false)
    const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set())
    const [isNavigating, setIsNavigating] = useState(false)

    // Check for autoOpenData to restore changes dialog when navigating back
    useEffect(() => {
        // @ts-ignore - custom location state property
        const autoOpenData = location.autoOpenData
        // The data could be in autoOpenData.data or autoOpenData.autoOpenData depending on how we got here
        const navData = autoOpenData?.autoOpenData || autoOpenData?.data
        if (navData?.openChangesForRoadmap) {
            setChangesDialog({
                open: true,
                roadmap: navData.openChangesForRoadmap,
            })
            // Clear the autoOpenData to prevent re-triggering
            // @ts-ignore
            location.autoOpenData = null
        }
    }, [location])

    // Fetch changes when dialog is open (use 1000 as "all" limit)
    const { data: changesData, isLoading: changesLoading } = useAdminRoadmapChangesQuery(
        changesDialog.roadmap?._id || '',
        changesDialog.open && !!changesDialog.roadmap?._id,
        showAllChanges ? 1000 : 50
    )
    const changes = changesData?.data?.changes || []

    // Reset state when dialog closes
    const handleCloseChangesDialog = () => {
        setChangesDialog({ open: false, roadmap: null })
        setShowAllChanges(false)
        setExpandedFields(new Set())
    }

    // Handle navigation to item detail view
    const handleNavigateToItem = async (change: RoadmapChange) => {
        // Skip Evidence Gaps - they don't have standalone detail views
        if (change.typeShort === 'Gap') return

        if (!changesDialog.roadmap?._id) return

        setIsNavigating(true)

        try {
            const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })

            // Fetch full strategy object
            const strategyResponse = await callAPI(`/api/strategy/${changesDialog.roadmap._id}`, 'get', token)

            if (!strategyResponse?.data) {
                console.error('Failed to fetch strategy')
                return
            }

            // Switch to the correct roadmap
            dispatch(setStrategy(strategyResponse.data))

            let itemData: Record<string, unknown> | null = null

            // If we have a contentId, fetch the full item directly
            if (change.contentId) {
                if (change.typeShort === 'VM') {
                    const vmResponse = await callAPI(`/api/value-message/${change.contentId}`, 'get', token)
                    if (vmResponse?.data) {
                        itemData = vmResponse.data
                    }
                } else if (change.typeShort === 'Ev') {
                    const evResponse = await callAPI(`/api/evidence/${change.contentId}`, 'get', token)
                    if (evResponse?.data) {
                        itemData = evResponse.data
                    }
                } else if (change.typeShort === 'Act') {
                    const actResponse = await callAPI(`/api/activity/${change.contentId}`, 'get', token)
                    if (actResponse?.data) {
                        itemData = actResponse.data
                    }
                }
            }

            // Fallback: If no contentId or fetch failed, try to find by exact name match
            if (!itemData && change.title && change.title !== '(no title)') {
                const searchName = change.title

                if (change.typeShort === 'VM') {
                    // Search VMs by name in this strategy
                    const vmsResponse = await callAPI(`/api/value-messages/${changesDialog.roadmap._id}`, 'get', token)
                    if (vmsResponse?.data) {
                        const matches = vmsResponse.data.filter((vm: { name?: string }) => vm.name === searchName)
                        if (matches.length === 1) {
                            itemData = matches[0]
                        }
                    }
                } else if (change.typeShort === 'Ev') {
                    // Search Evidence by name in this strategy
                    const evResponse = await callAPI(`/api/evidences/${changesDialog.roadmap._id}`, 'get', token)
                    if (evResponse?.data) {
                        const matches = evResponse.data.filter((ev: { name?: string }) => ev.name === searchName)
                        if (matches.length === 1) {
                            itemData = matches[0]
                        }
                    }
                } else if (change.typeShort === 'Act') {
                    // Search Activities by title in this strategy
                    const actResponse = await callAPI(`/api/activities/${changesDialog.roadmap._id}`, 'get', token)
                    if (actResponse?.data) {
                        const matches = actResponse.data.filter((act: { title?: string }) => act.title === searchName)
                        if (matches.length === 1) {
                            itemData = matches[0]
                        }
                    }
                }
            }

            // If we still don't have item data, can't navigate
            if (!itemData) {
                console.error('Could not find item to navigate to')
                setIsNavigating(false)
                return
            }

            // Close dialog
            handleCloseChangesDialog()

            // Determine route based on type
            const routes: Record<string, string> = {
                VM: '/narratives',
                Ev: '/evidence',
                Act: '/activities',
            }
            const pathname = routes[change.typeShort]

            if (pathname) {
                // Navigate with autoOpenData, include back navigation to admin with dialog open
                history.push({
                    pathname,
                    // @ts-ignore - custom location state property
                    autoOpenData: {
                        isAutoOpenPanel: true,
                        autoOpenData: itemData,
                        previousDataStack: [
                            {
                                path: '/admin',
                                isAutoOpenPanel: false,
                                data: {
                                    openChangesForRoadmap: changesDialog.roadmap,
                                    tabIndex: 1, // Analytics tab
                                },
                            },
                        ],
                    },
                })
            }
        } catch (err) {
            console.error('Error navigating to item:', err)
        } finally {
            setIsNavigating(false)
        }
    }

    // Helper to truncate text
    const truncateText = (text: string, maxLength: number = 100) => {
        if (text.length <= maxLength) return { text, truncated: false }
        return { text: text.substring(0, maxLength) + '...', truncated: true }
    }

    // Toggle field expansion
    const toggleFieldExpand = (fieldKey: string) => {
        setExpandedFields((prev) => {
            const next = new Set(prev)
            if (next.has(fieldKey)) {
                next.delete(fieldKey)
            } else {
                next.add(fieldKey)
            }
            return next
        })
    }

    const organizations = data?.data?.organizations || []
    const totalRoadmaps = data?.data?.totalRoadmaps || 0

    // Calculate max values for scaling
    const allRoadmaps = organizations.flatMap((org) => org.roadmaps)
    const maxContent = Math.max(...allRoadmaps.map((r) => r.totalContent), 1)

    // Filter and sort
    const filteredOrgs = selectedOrg ? organizations.filter((org) => org.organizationId === selectedOrg) : organizations

    // Sort orgs by selected criteria
    const sortedOrgs = [...filteredOrgs]
        .sort((a, b) => {
            switch (sortOrgsBy) {
                case 'activity':
                    // Most active first
                    return b.totalActivityScore - a.totalActivityScore
                case 'favorites':
                    // Favorites first, then by name
                    if (a.isFavorite && !b.isFavorite) return -1
                    if (!a.isFavorite && b.isFavorite) return 1
                    return a.organizationName.localeCompare(b.organizationName)
                case 'name':
                default:
                    return a.organizationName.localeCompare(b.organizationName)
            }
        })
        .map((org) => ({
            ...org,
            roadmaps: [...org.roadmaps].sort((a, b) => {
                switch (sortBy) {
                    case 'size':
                        return b.totalContent - a.totalContent
                    case 'activity':
                        return b.recentChanges - a.recentChanges
                    case 'name':
                        return a.product.localeCompare(b.product)
                    default:
                        return 0
                }
            }),
        }))

    // Calculate bubble size using square root scaling for better visual differentiation
    // Square root makes area proportional to value (perceptually accurate for comparing quantities)
    const getBubbleSize = (content: number) => {
        if (content === 0) return 24
        const ratio = content / maxContent
        // Square root scaling: sqrt(ratio) makes area proportional to content
        const scaledRatio = Math.sqrt(ratio)
        // Range: 24px min to 120px max for better size differentiation
        return Math.max(24, Math.min(120, 24 + scaledRatio * 96))
    }

    // Get color based on last activity date using smooth gradient (orange=hot/recent -> blue=cold/stale)
    // Matches legend: Now (0) -> 1mo (30) -> 3mo (90) -> 5mo (150) -> 6mo+ (180)
    const getActivityColor = (lastActivity: string, isActive: boolean) => {
        if (!isActive) return 'grey.400'
        const now = new Date()
        const lastDate = new Date(lastActivity)
        const daysSince = Math.floor((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24))

        // Gradient from hot (0 days) to cold (180+ days)
        const maxDays = 180
        const t = Math.min(daysSince / maxDays, 1) // 0 to 1 normalized

        // Interpolate through color stops matching the legend
        const lerp = (a: number, b: number, t: number) => a + (b - a) * t
        const hexToRgb = (hex: string) => ({
            r: parseInt(hex.slice(1, 3), 16),
            g: parseInt(hex.slice(3, 5), 16),
            b: parseInt(hex.slice(5, 7), 16),
        })
        const rgbToHex = (r: number, g: number, b: number) =>
            `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`

        // Color stops: Now=0, 1mo=0.17, 3mo=0.5, 5mo=0.83, 6mo+=1
        const colors = [
            { stop: 0, hex: '#ff6b35' }, // Hot orange (Now)
            { stop: 0.17, hex: '#ffb347' }, // Warm orange (1 month)
            { stop: 0.5, hex: '#e8eef3' }, // Cool off-white (3 months)
            { stop: 0.83, hex: '#b3d9f2' }, // Light blue (5 months)
            { stop: 1, hex: '#a3cfe8' }, // Cold pale blue (6+ months)
        ]

        // Find which segment we're in and interpolate
        for (let i = 0; i < colors.length - 1; i++) {
            if (t <= colors[i + 1].stop) {
                const segmentT = (t - colors[i].stop) / (colors[i + 1].stop - colors[i].stop)
                const c1 = hexToRgb(colors[i].hex)
                const c2 = hexToRgb(colors[i + 1].hex)
                return rgbToHex(lerp(c1.r, c2.r, segmentT), lerp(c1.g, c2.g, segmentT), lerp(c1.b, c2.b, segmentT))
            }
        }
        return colors[colors.length - 1].hex
    }

    const formatDate = (dateString: string) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return ''
        const now = new Date()
        const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))
        if (diffDays === 0) return 'Today'
        if (diffDays === 1) return 'Yesterday'
        if (diffDays < 7) return `${diffDays} days ago`
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
        return date.toLocaleDateString()
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <LinearProgress sx={{ width: 200 }} />
            </Box>
        )
    }

    if (error) {
        return <Alert severity="error">Failed to load analytics data</Alert>
    }

    return (
        <Box>
            {/* Controls */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    select
                    size="small"
                    label="Organization"
                    value={selectedOrg || ''}
                    onChange={(e) => setSelectedOrg(e.target.value || null)}
                    sx={{ minWidth: 200 }}
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                >
                    <option value="">All Organizations</option>
                    {organizations.map((org) => (
                        <option key={org.organizationId} value={org.organizationId}>
                            {org.organizationName} ({org.roadmaps.length})
                        </option>
                    ))}
                </TextField>

                <TextField
                    select
                    size="small"
                    label="Sort Orgs By"
                    value={sortOrgsBy}
                    onChange={(e) => setSortOrgsBy(e.target.value as 'activity' | 'name' | 'favorites')}
                    sx={{ minWidth: 150 }}
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                >
                    <option value="activity">Activity</option>
                    <option value="favorites">Pinned</option>
                    <option value="name">Name</option>
                </TextField>

                <TextField
                    select
                    size="small"
                    label="Sort Roadmaps"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'size' | 'activity' | 'name')}
                    sx={{ minWidth: 150 }}
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                >
                    <option value="size">Contents</option>
                    <option value="activity">Activity</option>
                    <option value="name">Name</option>
                </TextField>

                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel shrink>Event Types</InputLabel>
                    <Select
                        multiple
                        value={selectedActionTypes}
                        onChange={(e) =>
                            setSelectedActionTypes(
                                typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                            )
                        }
                        input={<OutlinedInput notched label="Event Types" />}
                        renderValue={(selected) =>
                            selected.length === ALL_ACTION_TYPES.length ? 'All' : `${selected.length} selected`
                        }
                        MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                    >
                        {ALL_ACTION_TYPES.map((type) => (
                            <MenuItem key={type} value={type} dense>
                                <Checkbox checked={selectedActionTypes.includes(type)} size="small" />
                                <ListItemText primary={ACTION_TYPE_LABELS[type]} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={excludeInternal}
                            onChange={(e) => setExcludeInternal(e.target.checked)}
                        />
                    }
                    label={<Typography variant="caption">Exclude Arysana/Aesara activity</Typography>}
                />

                <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                    <Chip label={`${totalRoadmaps} roadmaps`} variant="outlined" />
                    <Chip label={`${organizations.length} orgs`} variant="outlined" />
                </Box>
            </Box>

            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3, p: 2, bgcolor: 'figma.grayscale.table1', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="caption" fontWeight={600}>
                        Last Activity:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box
                            sx={{
                                width: 240,
                                height: 16,
                                borderRadius: 1,
                                background:
                                    'linear-gradient(to right, #ff6b35, #ffb347 25%, #e8eef3 50%, #b3d9f2 75%, #a3cfe8)',
                            }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: 240 }}>
                            <Typography variant="caption" sx={{ fontSize: 10 }}>
                                Now
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: 10 }}>
                                1mo
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: 10 }}>
                                3mo
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: 10 }}>
                                5mo
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: 10 }}>
                                6mo+
                            </Typography>
                        </Box>
                        <Tooltip
                            title="Activity includes: content edits (create, update, delete), AI uploads & summaries, MAIA chat, PowerPoint exports, snapshots, and roadmap setting changes. Passive views are not counted."
                            placement="bottom"
                            arrow
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: 10,
                                    color: 'text.secondary',
                                    cursor: 'default',
                                    textDecoration: 'underline dotted',
                                    width: 'fit-content',
                                }}
                            >
                                What counts as activity?
                            </Typography>
                        </Tooltip>
                    </Box>
                </Box>
            </Box>

            {/* Bubble visualization */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {sortedOrgs.map((org) => (
                    <Paper key={org.organizationId} sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            {org.isFavorite && (
                                <SvgIcon
                                    component={PushPinSvg}
                                    inheritViewBox
                                    sx={{ fontSize: 16, color: 'figma.primary' }}
                                />
                            )}
                            <Typography variant="subtitle1" fontWeight={600}>
                                {org.organizationName}
                            </Typography>
                            <Chip label={`${org.roadmaps.length} roadmaps`} size="small" />
                            {org.totalActivityScore > 0 &&
                                (() => {
                                    // Get the most recent activity from all roadmaps in the org
                                    const mostRecentActivity = org.roadmaps
                                        .filter((r: AnalyticsRoadmap) => r.lastActivity)
                                        .map((r: AnalyticsRoadmap) => new Date(r.lastActivity).getTime())
                                        .sort((a: number, b: number) => b - a)[0]
                                    const activityColor = mostRecentActivity
                                        ? getActivityColor(new Date(mostRecentActivity).toISOString(), true)
                                        : '#e8eef3'

                                    return (
                                        <>
                                            <Tooltip
                                                title={`${org.userEventCount} user events + ${org.totalRoadmapChanges} roadmap changes in last 30 days`}
                                            >
                                                <Chip
                                                    label={`${org.totalActivityScore} recent event${org.totalActivityScore === 1 ? '' : 's'}`}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 500,
                                                        bgcolor: activityColor,
                                                        color:
                                                            mostRecentActivity &&
                                                            Date.now() - mostRecentActivity < 30 * 24 * 60 * 60 * 1000
                                                                ? 'white'
                                                                : 'text.primary',
                                                    }}
                                                />
                                            </Tooltip>
                                            <Tooltip title="Active users in last 30 days">
                                                <Chip
                                                    label={`${org.activeUserCount} recent user${org.activeUserCount === 1 ? '' : 's'}`}
                                                    size="small"
                                                    variant="outlined"
                                                    color="info"
                                                />
                                            </Tooltip>
                                        </>
                                    )
                                })()}
                            {org.totalActivityScore === 0 && (
                                <Chip
                                    label="No recent activity"
                                    size="small"
                                    variant="outlined"
                                    sx={{ opacity: 0.6 }}
                                />
                            )}
                        </Box>

                        {/* Active roadmaps */}
                        {(() => {
                            const activeRoadmaps = org.roadmaps.filter((r: AnalyticsRoadmap) => r.isActive !== false)
                            const archivedRoadmaps = org.roadmaps.filter((r: AnalyticsRoadmap) => r.isActive === false)

                            return (
                                <>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 3,
                                            alignItems: 'flex-start',
                                            minHeight: 80,
                                        }}
                                    >
                                        {activeRoadmaps.map((roadmap: AnalyticsRoadmap) => {
                                            const size = getBubbleSize(roadmap.totalContent)
                                            const color = getActivityColor(roadmap.lastActivity, true)

                                            return (
                                                <Tooltip
                                                    key={roadmap._id}
                                                    title={
                                                        <Box sx={{ p: 0.5 }}>
                                                            <Box sx={{ fontSize: 11 }}>
                                                                <div>
                                                                    VM: {roadmap.vmCount} | Ev: {roadmap.evidenceCount}{' '}
                                                                    | Act: {roadmap.activityCount}
                                                                </div>
                                                                <div>Total: {roadmap.totalContent} items</div>
                                                                <div>
                                                                    Recent: {roadmap.recentChanges} interactions (30d)
                                                                </div>
                                                                <div>
                                                                    Last activity:{' '}
                                                                    <Tooltip
                                                                        title={
                                                                            roadmap.lastActivity
                                                                                ? new Date(
                                                                                      roadmap.lastActivity
                                                                                  ).toLocaleString(undefined, {
                                                                                      timeZoneName: 'short',
                                                                                  })
                                                                                : ''
                                                                        }
                                                                    >
                                                                        <span>{formatDate(roadmap.lastActivity)}</span>
                                                                    </Tooltip>
                                                                </div>
                                                            </Box>
                                                        </Box>
                                                    }
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            maxWidth: 100,
                                                        }}
                                                    >
                                                        <Box
                                                            onClick={() => setChangesDialog({ open: true, roadmap })}
                                                            sx={{
                                                                width: size,
                                                                height: size,
                                                                borderRadius: '50%',
                                                                bgcolor: color,
                                                                cursor: 'pointer',
                                                                transition: 'transform 0.2s',
                                                                '&:hover': {
                                                                    transform: 'scale(1.1)',
                                                                    boxShadow: 3,
                                                                },
                                                            }}
                                                        />
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                mt: 0.5,
                                                                textAlign: 'center',
                                                                fontSize: 11,
                                                                lineHeight: 1.2,
                                                                fontWeight: 500,
                                                                maxWidth: 90,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                            }}
                                                        >
                                                            {roadmap.product}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontSize: 10,
                                                                color: 'figma.grayscale.textHier2',
                                                                textAlign: 'center',
                                                            }}
                                                        >
                                                            {roadmap.indication}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            )
                                        })}

                                        {activeRoadmaps.length === 0 && archivedRoadmaps.length === 0 && (
                                            <Typography variant="body2" color="figma.grayscale.textHier2">
                                                No roadmaps
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Archived roadmaps section */}
                                    {archivedRoadmaps.length > 0 && (
                                        <Box
                                            sx={{
                                                mt: 2,
                                                pt: 2,
                                                borderTop: '1px dashed',
                                                borderColor: 'figma.grayscale.stroke',
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="figma.grayscale.textHier2"
                                                sx={{ display: 'block', mb: 1 }}
                                            >
                                                Archived ({archivedRoadmaps.length})
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 2,
                                                    alignItems: 'flex-start',
                                                }}
                                            >
                                                {archivedRoadmaps.map((roadmap: AnalyticsRoadmap) => {
                                                    const size = Math.max(16, getBubbleSize(roadmap.totalContent) * 0.6)

                                                    return (
                                                        <Tooltip
                                                            key={roadmap._id}
                                                            title={
                                                                <Box sx={{ p: 0.5 }}>
                                                                    <Box sx={{ fontSize: 11 }}>
                                                                        <div>
                                                                            VM: {roadmap.vmCount} | Ev:{' '}
                                                                            {roadmap.evidenceCount} | Act:{' '}
                                                                            {roadmap.activityCount}
                                                                        </div>
                                                                        <div>Total: {roadmap.totalContent} items</div>
                                                                        <div>
                                                                            Last activity:{' '}
                                                                            <Tooltip
                                                                                title={
                                                                                    roadmap.lastActivity
                                                                                        ? new Date(
                                                                                              roadmap.lastActivity
                                                                                          ).toLocaleString(undefined, {
                                                                                              timeZoneName: 'short',
                                                                                          })
                                                                                        : ''
                                                                                }
                                                                            >
                                                                                <span>
                                                                                    {formatDate(roadmap.lastActivity)}
                                                                                </span>
                                                                            </Tooltip>
                                                                        </div>
                                                                    </Box>
                                                                </Box>
                                                            }
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'center',
                                                                    maxWidth: 80,
                                                                    opacity: 0.6,
                                                                }}
                                                            >
                                                                <Box
                                                                    onClick={() =>
                                                                        setChangesDialog({ open: true, roadmap })
                                                                    }
                                                                    sx={{
                                                                        width: size,
                                                                        height: size,
                                                                        borderRadius: '50%',
                                                                        bgcolor: 'figma.grayscale.stroke',
                                                                        border: '2px dashed',
                                                                        borderColor: 'grey.400',
                                                                        cursor: 'pointer',
                                                                        '&:hover': { opacity: 1 },
                                                                    }}
                                                                />
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        mt: 0.5,
                                                                        textAlign: 'center',
                                                                        fontSize: 10,
                                                                        color: 'figma.grayscale.textHier2',
                                                                        maxWidth: 70,
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    {roadmap.product}
                                                                </Typography>
                                                            </Box>
                                                        </Tooltip>
                                                    )
                                                })}
                                            </Box>
                                        </Box>
                                    )}
                                </>
                            )
                        })()}
                    </Paper>
                ))}
            </Box>

            {/* Changes Dialog */}
            <Dialog open={changesDialog.open} onClose={handleCloseChangesDialog} maxWidth="md" fullWidth>
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                            <Typography variant="h6" component="span">
                                {changesDialog.roadmap?.product}
                            </Typography>
                            {changesDialog.roadmap?.indication && (
                                <Typography variant="body2" color="figma.grayscale.textHier2">
                                    {changesDialog.roadmap.indication}
                                </Typography>
                            )}
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showAllChanges}
                                    onChange={(e) => setShowAllChanges(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={
                                <Typography variant="caption">
                                    Show all ({showAllChanges ? 'up to 1000' : 'showing 50'})
                                </Typography>
                            }
                        />
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {isNavigating && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                bgcolor: 'rgba(255, 255, 255, 0.8)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10,
                            }}
                        >
                            <Box sx={{ textAlign: 'center' }}>
                                <CircularProgress size={32} />
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Switching roadmap...
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {changesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : changes.length === 0 ? (
                        <Typography color="figma.grayscale.textHier2" sx={{ py: 2, textAlign: 'center' }}>
                            No recent changes found
                        </Typography>
                    ) : (
                        <>
                            <Typography variant="body2" color="figma.grayscale.textHier2" sx={{ mb: 1 }}>
                                Showing {changes.length} event{changes.length !== 1 ? 's' : ''}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {changes.map((change: RoadmapChange) => {
                                    // Check if we can navigate to this item
                                    // Need either a contentId OR a title to search by (for creates without contentId)
                                    // Don't allow navigation for deletes (item no longer exists)
                                    const hasNavigableId =
                                        !!change.contentId || (change.title && change.title !== '(no title)')
                                    const isClickable =
                                        ['VM', 'Ev', 'Act'].includes(change.typeShort) &&
                                        hasNavigableId &&
                                        change.action !== 'delete'

                                    return (
                                        <Box
                                            key={change._id}
                                            onClick={isClickable ? () => handleNavigateToItem(change) : undefined}
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 1,
                                                bgcolor: 'figma.grayscale.table1',
                                                borderLeft: '3px solid',
                                                borderLeftColor:
                                                    change.action === 'create'
                                                        ? 'success.main'
                                                        : change.action === 'delete'
                                                          ? 'error.main'
                                                          : 'figma.primary',
                                                cursor: isClickable ? 'pointer' : 'default',
                                                transition: 'all 0.15s ease',
                                                '&:hover': isClickable
                                                    ? {
                                                          bgcolor: 'figma.grayscale.table2',
                                                          transform: 'translateX(2px)',
                                                      }
                                                    : {},
                                            }}
                                        >
                                            {/* Header row */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Chip
                                                    label={change.typeShort}
                                                    size="small"
                                                    color={
                                                        change.typeShort === 'VM'
                                                            ? 'primary'
                                                            : change.typeShort === 'Ev'
                                                              ? 'success'
                                                              : change.typeShort === 'Act'
                                                                ? 'warning'
                                                                : 'default'
                                                    }
                                                    sx={{ height: 20, fontSize: 11 }}
                                                />
                                                <Chip
                                                    label={change.action}
                                                    size="small"
                                                    variant="outlined"
                                                    color={
                                                        change.action === 'create'
                                                            ? 'success'
                                                            : change.action === 'delete'
                                                              ? 'error'
                                                              : 'default'
                                                    }
                                                    sx={{ height: 20, fontSize: 11, textTransform: 'capitalize' }}
                                                />
                                                <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
                                                    {change.title}
                                                </Typography>
                                                <Typography variant="caption" color="figma.grayscale.textHier2">
                                                    {change.timestamp && !isNaN(new Date(change.timestamp).getTime())
                                                        ? `${new Date(change.timestamp).toLocaleDateString()} ${new Date(change.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}`
                                                        : ''}
                                                </Typography>
                                                {isClickable && (
                                                    <Tooltip title="Open detail view">
                                                        <Chip
                                                            label="Open →"
                                                            size="small"
                                                            variant="outlined"
                                                            color="primary"
                                                            sx={{
                                                                height: 20,
                                                                fontSize: 10,
                                                                ml: 1,
                                                                opacity: 0.7,
                                                                '&:hover': { opacity: 1 },
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Box>

                                            {/* User */}
                                            {change.user && (
                                                <Typography
                                                    variant="caption"
                                                    color="figma.grayscale.textHier2"
                                                    sx={{ display: 'block', mb: 0.5 }}
                                                >
                                                    by {change.user.name || change.user.email}
                                                </Typography>
                                            )}

                                            {/* Show linking changes for UPDATEs without field changes */}
                                            {change.action === 'update' &&
                                                (!change.changedFields || change.changedFields.length === 0) &&
                                                change.linkingChanges &&
                                                change.linkingChanges.length > 0 && (
                                                    <Typography
                                                        variant="caption"
                                                        color="figma.grayscale.textHier2"
                                                        sx={{ mt: 0.5 }}
                                                    >
                                                        Linking change:{' '}
                                                        {change.linkingChanges.map((lc, i) => (
                                                            <span key={i}>
                                                                {lc.type === 'added' ? '+' : '-'}
                                                                {lc.count} {lc.field}
                                                                {i < (change.linkingChanges?.length || 0) - 1
                                                                    ? ', '
                                                                    : ''}
                                                            </span>
                                                        ))}
                                                    </Typography>
                                                )}

                                            {/* Show "No field changes" only if no linking changes either */}
                                            {change.action === 'update' &&
                                                (!change.changedFields || change.changedFields.length === 0) &&
                                                (!change.linkingChanges || change.linkingChanges.length === 0) && (
                                                    <Typography
                                                        variant="caption"
                                                        color="figma.grayscale.textHier2"
                                                        sx={{ mt: 0.5, fontStyle: 'italic' }}
                                                    >
                                                        {change.hasEmptyMetadata
                                                            ? 'Saved without changes'
                                                            : 'No field changes detected'}
                                                    </Typography>
                                                )}

                                            {/* Inline changes for UPDATE events */}
                                            {change.action === 'update' &&
                                                change.changedFields &&
                                                change.changedFields.length > 0 &&
                                                (() => {
                                                    const changeKey = `${change._id}-details`
                                                    const isExpanded = expandedFields.has(changeKey)
                                                    const fieldNames = change.changedFields
                                                        .map((f) => f.field)
                                                        .join(', ')

                                                    return (
                                                        <Box
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                toggleFieldExpand(changeKey)
                                                            }}
                                                            sx={{
                                                                mt: 0.5,
                                                                cursor: 'pointer',
                                                                '&:hover': { bgcolor: 'figma.grayscale.table2' },
                                                                borderRadius: 0.5,
                                                                p: 0.5,
                                                                ml: -0.5,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                color="figma.grayscale.textHier2"
                                                            >
                                                                {change.changedFields.length} field
                                                                {change.changedFields.length !== 1
                                                                    ? 's'
                                                                    : ''} changed: {fieldNames}
                                                                <span
                                                                    style={{
                                                                        marginLeft: 4,
                                                                        fontSize: 10,
                                                                        opacity: 0.7,
                                                                    }}
                                                                >
                                                                    {isExpanded ? '(hide)' : '(show)'}
                                                                </span>
                                                            </Typography>

                                                            {isExpanded && (
                                                                <Box sx={{ mt: 1 }}>
                                                                    {change.changedFields.map((field, idx) => {
                                                                        const fromStr = String(field.from || '(empty)')
                                                                        const toStr = String(field.to || '(empty)')

                                                                        return (
                                                                            <Box key={idx} sx={{ mb: 1 }}>
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    color="figma.grayscale.textHier2"
                                                                                    sx={{
                                                                                        fontWeight: 500,
                                                                                        textTransform: 'capitalize',
                                                                                    }}
                                                                                >
                                                                                    {field.field}:
                                                                                </Typography>
                                                                                <Box
                                                                                    sx={{
                                                                                        display: 'flex',
                                                                                        alignItems: 'flex-start',
                                                                                        gap: 1,
                                                                                        mt: 0.25,
                                                                                    }}
                                                                                >
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        sx={{
                                                                                            color: 'error.dark',
                                                                                            textDecoration:
                                                                                                'line-through',
                                                                                            bgcolor: 'error.50',
                                                                                            px: 0.5,
                                                                                            borderRadius: 0.5,
                                                                                            flex: 1,
                                                                                            wordBreak: 'break-word',
                                                                                        }}
                                                                                    >
                                                                                        {fromStr}
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        color="figma.grayscale.textHier2"
                                                                                        sx={{ flexShrink: 0 }}
                                                                                    >
                                                                                        →
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        sx={{
                                                                                            color: 'success.dark',
                                                                                            bgcolor: 'success.50',
                                                                                            px: 0.5,
                                                                                            borderRadius: 0.5,
                                                                                            flex: 1,
                                                                                            wordBreak: 'break-word',
                                                                                        }}
                                                                                    >
                                                                                        {toStr}
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Box>
                                                                        )
                                                                    })}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )
                                                })()}
                                        </Box>
                                    )
                                })}
                            </Box>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    )
}

export default RoadmapAnalyticsTab
